import {
  faArrowRightArrowLeft,
  faBoxCheck,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Action,
  Badge,
  Card,
  Feed,
  OrderLineItem,
  RelativeDateTime,
} from 'corso-admin-ui';
import { CrewMerchantUi, isTruthy } from 'corso-types';
import { match } from 'ts-pattern';
import { formatter } from 'corso-web-lib';

type Lineage = CrewMerchantUi.ClaimOrderLineage;
type LineageOrder = Lineage['originalOrder'];
type LineageClaim = Lineage['inputClaim'];

const orderName = (order: LineageOrder) =>
  order.nameFromPlatform ?? order.orderNumber ?? `Order ${order.id}`;

function OrderLink({ order }: { order: LineageOrder }) {
  if (order.wasResolvedExternally) return <span>{orderName(order)}</span>;
  return (
    <Action variant="link" to={order.platformAdminUrl} target="_blank">
      {orderName(order)}
    </Action>
  );
}

function OrderRow({
  order,
  currencyCode,
  isRoot,
}: {
  order: LineageOrder;
  currencyCode: string;
  isRoot: boolean;
}) {
  return (
    <div className="flex flex-auto flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <p className="font-semibold">
            <OrderLink order={order} />
          </p>
          {isRoot && <Badge variant="info">Originating Order</Badge>}
          {order.wasResolvedExternally && <Badge>Resolved Outside Corso</Badge>}
        </span>
        <RelativeDateTime
          dateTime={order.createdOn}
          className="flex-none text-xs text-neutral-500"
        />
      </div>
      {order.lineItems.length > 0 && (
        <ul className="flex flex-col gap-2">
          {order.lineItems.map((li) => (
            <li key={li.id}>
              <OrderLineItem
                size="sm"
                title={li.name}
                src={li.imgUrl}
                quantity={li.quantity}
                sku={li.sku}
                price={{
                  currencyCode,
                  unitPrice: li.unitPrice,
                  unitTax: li.unitTax,
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClaimRow({
  claim,
  currencyCode,
  isInput,
}: {
  claim: LineageClaim;
  currencyCode: string;
  isInput: boolean;
}) {
  const { customerPayment } = claim;
  const moneyParts = [
    customerPayment && (
      <span key="payment" className="flex items-center gap-1">
        Stripe {formatter.currency(customerPayment.amount, currencyCode)}
        <Badge>{customerPayment.status}</Badge>
      </span>
    ),
    claim.refundsIssued > 0 && (
      <span key="refunds">
        Refunded {formatter.currency(claim.refundsIssued, currencyCode)}
      </span>
    ),
    claim.giftCardsIssued > 0 && (
      <span key="giftCards">
        Gift card {formatter.currency(claim.giftCardsIssued, currencyCode)}
      </span>
    ),
  ].filter(isTruthy);

  return (
    <div className="flex flex-auto flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <p className="font-semibold">#{claim.externalId}</p>
          {isInput && <Badge variant="info">Current Claim</Badge>}
          {claim.lineItems.some((li) => li.isShopNow) && (
            <Badge variant="info">Shop Now</Badge>
          )}
        </span>
        <RelativeDateTime
          dateTime={claim.createdOn}
          className="flex-none text-xs text-neutral-500"
        />
      </div>
      {moneyParts.length > 0 && (
        <div className="flex flex-wrap gap-3 text-xs text-neutral-600 tabular-nums">
          {moneyParts}
        </div>
      )}
      {claim.lineItems.length > 0 && (
        <ul className="flex flex-col gap-2">
          {claim.lineItems.map((li) => (
            <li key={li.id}>
              <OrderLineItem
                size="sm"
                title={li.name}
                src={li.imgUrl}
                quantity={li.quantity}
                sku={li.sku}
                price={{ currencyCode, unitPrice: li.unitPrice }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Modal contents; only meaningful when the claim sits on an exchange chain. */
export function ClaimLineageHistory({ lineage }: { lineage: Lineage }) {
  if (lineage.hops.length === 0) return null;

  const events = [
    {
      id: `order-${lineage.originalOrder.id}`,
      kind: 'order' as const,
      order: lineage.originalOrder,
      isRoot: true,
    },
    ...lineage.hops.flatMap((hop) => [
      {
        id: `claim-${hop.claim.id}`,
        kind: 'claim' as const,
        claim: hop.claim,
        isInput: false,
      },
      {
        id: `order-${hop.exchangeOrder.id}`,
        kind: 'order' as const,
        order: hop.exchangeOrder,
        isRoot: false,
      },
    ]),
    {
      id: `claim-${lineage.inputClaim.id}`,
      kind: 'claim' as const,
      claim: lineage.inputClaim,
      isInput: true,
    },
  ].reverse();

  return (
    <div className="flex flex-col gap-2">
      <Card.Heading>Order history</Card.Heading>
      <Feed
        events={events}
        renderIcon={(event) =>
          match(event)
            .with({ kind: 'order' }, () => (
              <FontAwesomeIcon icon={faBoxCheck} />
            ))
            .with({ kind: 'claim' }, () => (
              <FontAwesomeIcon icon={faArrowRightArrowLeft} />
            ))
            .exhaustive()
        }
      >
        {(event) =>
          match(event)
            .with({ kind: 'order' }, ({ order, isRoot }) => (
              <OrderRow
                order={order}
                currencyCode={lineage.currencyCode}
                isRoot={isRoot}
              />
            ))
            .with({ kind: 'claim' }, ({ claim, isInput }) => (
              <ClaimRow
                claim={claim}
                currencyCode={lineage.currencyCode}
                isInput={isInput}
              />
            ))
            .exhaustive()
        }
      </Feed>
    </div>
  );
}
